import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_COLOR = {
  Released: '#46BB89', Draft: '#94a3b8', 'In Review': '#60a5fa',
  Obsolete: '#EC2124', Superseded: '#f59e0b',
};

const referenceIdentity = item => JSON.stringify([
  item.documentNumber || item.refId || item._id || item.url || '', item.revision || '',
]);
const referenceAliases = item => [
  item.documentNumber && JSON.stringify(['document',item.documentNumber,item.revision || '']),
  (item.refId || item._id) && JSON.stringify(['id',item.refId || item._id,item.revision || '']),
].filter(Boolean);

/**
 * Resource picker with instant dropdown on focus.
 * Fetches all Released items on first open, then filters client-side.
 * Falls back to live search for queries that might match non-standard names.
 *
 * Props:
 *   endpoint     — API path, e.g. '/quality/jigs'
 *   responseKey  — key in API response, e.g. 'jigs', 'items', 'entries'
 *   items        — current linked items: [{refId, name, documentNumber}]
 *   onChange     — (newItems) => void
 *   getToken     — Clerk getToken function
 *   color        — accent colour string
 *   label        — display label for placeholder text
 *   disabled     — read-only mode
 *   detailPath   — enables ↗ link to item detail
 */
export default function ResourcePicker({
  endpoint, responseKey, items = [], onChange,
  getToken, color = '#60a5fa', label = 'resource',
  disabled = false, detailPath,
}) {
  const [search, setSearch]     = useState('');
  const [open, setOpen]         = useState(false);
  const [allItems, setAllItems] = useState([]);   // full list fetched once
  const [loading, setLoading]   = useState(false);
  const hasFetched               = useRef(false);
  const containerRef             = useRef(null);
  const inputRef                 = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handle = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Fetch all available items once on first open
  const fetchAll = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await window.fetch(
        `${API_BASE}${endpoint}?limit=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setAllItems(data[responseKey] || []);
    } catch {} finally { setLoading(false); }
  }, [getToken, endpoint, responseKey]);

  const handleFocus = () => {
    fetchAll();
    setOpen(true);
  };

  // Client-side filter
  const q = search.toLowerCase().trim();
  const alreadyLinked = new Set((items || []).flatMap(referenceAliases));

  const filtered = allItems.filter(item => {
    if (referenceAliases(item).some(identity => alreadyLinked.has(identity))) return false;
    if (!q) return true;
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.documentNumber || '').toLowerCase().includes(q)
    );
  });

  // Group: Released first, then others
  const released = filtered.filter(i => i.currentStatus === 'Released');
  const others   = filtered.filter(i => i.currentStatus !== 'Released');
  const displayed = [...released, ...others];

  const add = item => {
    onChange([...(items || []), {
      refId: item._id,
      name: item.name,
      documentNumber: item.documentNumber,
      ...(item.revision ? { revision:item.revision } : {}),
      ...(item.url ? { url:item.url } : {}),
    }]);
    setSearch('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const remove = index => onChange((items || []).filter((_,i) => i !== index));
  const keyOccurrences = new Map();
  const linkedItems = (items || []).map((item,index) => {
    const identity = referenceIdentity(item);
    const occurrence = keyOccurrences.get(identity) || 0;
    keyOccurrences.set(identity,occurrence + 1);
    return { item, index, key:JSON.stringify([identity,occurrence]) };
  });

  const inp = {
    background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)',
    color: 'var(--ni-text)', borderRadius: 6, padding: '6px 10px',
    fontSize: 12, width: '100%', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div ref={containerRef}>
      {/* Linked items */}
      {linkedItems.map(({ item, index, key }) => (
        <div key={key} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', marginBottom: 4,
          background: color + '11', borderRadius: 6, border: `1px solid ${color}33`,
        }}>
          {detailPath && (
            <button type="button"
              onClick={() => window.open(`${detailPath}/${item.refId || encodeURIComponent(item.documentNumber)}${item.revision ? `?revision=${encodeURIComponent(item.revision)}` : ''}`, '_blank', 'noopener,noreferrer')}
              title="Open in new tab"
              style={{ background:'none', border:'none', color, cursor:'pointer', fontSize:11, padding:0, lineHeight:1 }}>
              ↗
            </button>
          )}
          <span style={{ fontSize:11, fontFamily:'monospace', color, flexShrink:0 }}>{item.documentNumber}</span>
          {item.revision && <span style={{ fontSize:11, fontFamily:'monospace', color }}>Rev. {item.revision}</span>}
          <span style={{ fontSize:12, flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item.name}</span>
          {!disabled && (
            <button type="button" aria-label={`Remove ${item.documentNumber || item.name || label}${item.revision ? ` ${item.revision}` : ''}`} onClick={() => remove(index)}
              style={{ background:'none', border:'none', color:'#EC2124', cursor:'pointer', fontSize:14, padding:'0 2px', lineHeight:1 }}>
              ×
            </button>
          )}
        </div>
      ))}

      {/* Input + dropdown */}
      {!disabled && (
        <div style={{ position:'relative' }}>
          <div style={{ position:'relative' }}>
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={handleFocus}
              onKeyDown={e => {
                if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                if (e.key === 'Enter' && displayed.length === 1) { e.preventDefault(); add(displayed[0]); }
              }}
              placeholder={`Search or browse ${label}…`}
              style={{
                ...inp,
                borderColor: open ? color + '88' : 'var(--ni-border-soft)',
                paddingRight: 28,
              }}
            />
            {/* chevron / spinner */}
            <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
              color:'var(--ni-text-muted)', fontSize:10, pointerEvents:'none', userSelect:'none' }}>
              {loading ? '…' : open ? '▲' : '▼'}
            </span>
          </div>

          {open && (
            <div style={{
              position:'absolute', top:'calc(100% + 3px)', left:0, right:0, zIndex:300,
              background:'var(--ni-char)', border:`1px solid ${color}44`,
              borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,0.4)',
              maxHeight:240, overflowY:'auto',
            }}>
              {loading && (
                <div style={{ padding:'12px 12px', fontSize:12, color:'var(--ni-text-muted)' }}>
                  Loading {label}s…
                </div>
              )}

              {!loading && displayed.length === 0 && (
                <div style={{ padding:'12px 12px', fontSize:12, color:'var(--ni-text-muted)' }}>
                  {q
                    ? `No ${label}s match "${search}"`
                    : allItems.length === 0
                      ? `No ${label}s found. Create one first.`
                      : `All ${label}s are already linked.`
                  }
                </div>
              )}

              {!loading && displayed.length > 0 && (
                <>
                  {/* Section header if there are non-released items too */}
                  {released.length > 0 && others.length > 0 && (
                    <div style={{ padding:'6px 10px 2px', fontSize:10, fontWeight:700,
                      color:'var(--ni-text-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                      Released
                    </div>
                  )}

                  {released.map(item => (
                    <DropdownItem key={referenceIdentity(item)} item={item} color={color} onAdd={add} />
                  ))}

                  {others.length > 0 && released.length > 0 && (
                    <div style={{ padding:'6px 10px 2px', fontSize:10, fontWeight:700,
                      color:'var(--ni-text-muted)', textTransform:'uppercase', letterSpacing:'0.06em',
                      borderTop:'1px solid var(--ni-border-soft)', marginTop:2 }}>
                      Other
                    </div>
                  )}

                  {others.map(item => (
                    <DropdownItem key={referenceIdentity(item)} item={item} color={color} onAdd={add} />
                  ))}
                </>
              )}

              {!loading && allItems.length > 0 && (
                <div style={{ padding:'6px 10px', fontSize:10, color:'var(--ni-text-muted)',
                  borderTop:'1px solid rgba(255,255,255,0.05)' }}>
                  {displayed.length} of {allItems.length - alreadyLinked.size} available
                  {q && ` · type to filter`}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {disabled && (!items || items.length === 0) && (
        <span style={{ fontSize:12, color:'var(--ni-text-muted)' }}>—</span>
      )}
    </div>
  );
}

function DropdownItem({ item, color, onAdd }) {
  const [hovered, setHovered] = useState(false);
  const sc = STATUS_COLOR[item.currentStatus] || '#666';

  return (
    <div
      onMouseDown={e => { e.preventDefault(); onAdd(item); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 10px', cursor: 'pointer',
        background: hovered ? 'rgba(255,255,255,0.06)' : 'transparent',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <span style={{ fontSize:11, fontFamily:'monospace', color, flexShrink:0, minWidth:80 }}>
        {item.documentNumber}
      </span>
      <span style={{ fontSize:12, flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
        {item.name}
      </span>
      <span style={{ fontSize:10, fontWeight:700, color:sc, background:sc+'22',
        borderRadius:4, padding:'1px 6px', flexShrink:0, textTransform:'uppercase', letterSpacing:'0.04em' }}>
        {item.currentStatus}
      </span>
    </div>
  );
}
